// src/App.tsx

import { BrowserRouter, Routes, Route, Outlet, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext'; // Importa useAuth
import { PluginProvider } from './context/PluginContext';
import { CartProvider } from './context/CartContext';
import { ProtectedRoute } from './components/auth/ProtectedRoute';

import { Navbar } from './components/common/Navbar';
import { Footer } from './components/common/Footer';
import { FloatingDiscordButton } from './components/common/FloatingDiscordButton';
import { AdminLayout } from './components/admin/AdminLayout';
import { FullScreenLoader } from './components/common/FullScreenLoader'; // Importa o loader

// Importa todas as páginas
import HomePage from './pages/HomePage';
import PluginsPage from './pages/PluginsPage';
import PluginDetailPage from './pages/PluginDetailPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import UserLoginPage from './pages/UserLoginPage';
import AdminLoginPage from './pages/AdminLoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import AccountPage from './pages/AccountPage';
import RedeemPage from './pages/RedeemPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import NotFoundPage from './pages/NotFoundPage';
import UpdateLegacyPasswordPage from './pages/UpdateLegacyPasswordPage';
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import AdminPluginsPage from './pages/admin/AdminPluginsPage';
import AdminUsersPage from './pages/admin/AdminUsersPage';
import AdminUserEditPage from './pages/admin/AdminUserEditPage';
import AdminPluginEditPage from './pages/admin/AdminPluginEditPage';
import AdminPluginCreatePage from './pages/admin/AdminPluginCreatePage';
import AdminLicensesPage from './pages/admin/AdminLicensesPage';
import AdminCategoriesPage from './pages/admin/AdminCategoriesPage';
import AdminCouponsPage from './pages/admin/AdminCouponsPage';
import AdminGiftCardsPage from './pages/admin/AdminGiftCardsPage';
import AdminNewsletterPage from './pages/admin/AdminNewsletterPage';
import UnsubscribePage from './pages/UnsubscribePage';

import ScrollToTop from './components/common/ScrollToTop';
import { CookieConsentBanner } from './components/common/CookieConsentBanner';
import { NotificationProvider } from './context/NotificationContext';
import { NotificationBanner } from './components/common/NotificationBanner';

// Layout público
function PublicLayout() {
    const location = useLocation();
    const isPublicRoute = !location.pathname.startsWith('/admin') &&
        !['/login', '/admin/login', '/register'].includes(location.pathname);

    return (
        <div className="font-sans bg-background text-text-primary">
            {isPublicRoute && <Navbar />}
            <main className="min-h-screen"><Outlet /></main>
            {isPublicRoute && <Footer />}
            {isPublicRoute && <FloatingDiscordButton />}
        </div>
    );
}

// Componente que renderiza o conteúdo principal do app
function AppContent() {
    const { loading } = useAuth();

    // Mostra o loader em tela cheia enquanto a autenticação está sendo verificada
    if (loading) {
        return <FullScreenLoader />;
    }

    return (
        <>
            <NotificationBanner />
            <BrowserRouter>
                <ScrollToTop />
                <Routes>
                <Route element={<PublicLayout />}>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/plugins" element={<PluginsPage />} />
                    <Route path="/plugins/:slug" element={<PluginDetailPage />} />
                    <Route path="/cart" element={<CartPage />} />
                    <Route path="/termos" element={<TermsOfServicePage />} />
                    <Route path="/privacidade" element={<PrivacyPolicyPage />} />
                    <Route path="/login" element={<UserLoginPage />} />
                    <Route path="/register" element={<RegisterPage />} />
                    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                    <Route path="/reset-password" element={<ResetPasswordPage />} />
                    <Route path="/verify-email" element={<VerifyEmailPage />} />
                    <Route path="/unsubscribe" element={<UnsubscribePage />} />
                    <Route path="/atualizar-senha-legada" element={<UpdateLegacyPasswordPage />} />
                    <Route element={<ProtectedRoute />}>
                        <Route path="/checkout" element={<CheckoutPage />} />
                        <Route path="/conta" element={<AccountPage />} />
                        <Route path="/resgatar" element={<RedeemPage />} />
                    </Route>
                </Route>

                <Route path="/admin/login" element={<AdminLoginPage />} />

                <Route element={<ProtectedRoute adminOnly={true} />}>
                    <Route path="/admin" element={<AdminLayout />}>
                        <Route path="dashboard" element={<AdminDashboardPage />} />
                        <Route path="plugins" element={<AdminPluginsPage />} />
                        <Route path="plugins/new" element={<AdminPluginCreatePage />} />
                        <Route path="plugins/edit/:pluginId" element={<AdminPluginEditPage />} />
                        <Route path="users" element={<AdminUsersPage />} />
                        <Route path="users/edit/:userId" element={<AdminUserEditPage />} />
                        <Route path="licenses" element={<AdminLicensesPage />} />
                        <Route path="categories" element={<AdminCategoriesPage />} />
                        <Route path="coupons" element={<AdminCouponsPage />} />
                        <Route path="giftcards" element={<AdminGiftCardsPage />} />
                        <Route path="newsletter" element={<AdminNewsletterPage />} />
                    </Route>
                </Route>

                <Route path="*" element={<NotFoundPage />} />
            </Routes>
            <CookieConsentBanner />
        </BrowserRouter>
        </>
    );
}

function App() {
    return (
        <AuthProvider>
            <PluginProvider>
                <NotificationProvider>
                    <CartProvider>
                        <AppContent />
                    </CartProvider>
                </NotificationProvider>
            </PluginProvider>
        </AuthProvider>
    );
}

export default App;